import { ItemList } from 'modules/cm/pages/CaseDetailsPage/partials/Nav/Nav.types';
import React, { useCallback } from 'react';
import { Icon } from 'modules/af_cm_ui_kit';
import { CaseDetails } from 'modules/cm/pages/CaseDetailsPage/CaseDetailsPage.types';
import usePepperFieldError from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError';
import { ValidationError } from 'modules/cm/pages/CaseDetailsPage/Pepper/errors/PepperValidationError';
import { FIELDS } from 'modules/cm/fields/types';
import { FIELDS as INCOME_FIELDS } from 'modules/cm/domain/Case/Applicant/Income/Current/Current';
import { ErrorPath } from '../../hooks/useFieldError/useFieldError';

type ItemListType = ItemList<React.ComponentProps<typeof Icon>['type']>;

const isNotUndefinedErrorPath = (errorPath: ErrorPath): errorPath is NonNullable<ErrorPath> => {
  return errorPath !== undefined;
};

const getDefaultItems = (errorPaths: ErrorPath[]): ItemListType => {
  const isTabWithError = (tabId: keyof ValidationError) => {
    return errorPaths.filter(isNotUndefinedErrorPath).some((errorPath) =>
      Object.keys(errorPath).some((path) => {
        return path.startsWith(tabId);
      }),
    );
  };

  return [
    {
      icon: 'list',
      label: 'Loan details',
      to: ``,
      exact: true,
      hasError: isTabWithError('loanDetails'),
      items: [],
    },
    {
      icon: 'credit',
      label: 'Credit',
      to: '/credit',
      hasError: false,
      items: [],
    },
    {
      icon: 'home',
      label: 'Property',
      to: '/property',
      hasError: isTabWithError('property'),
      items: [],
    },
    {
      icon: 'list',
      label: 'Repayment strategy',
      to: `/repayment-strategy`,
      exact: true,
      hasError: isTabWithError('repaymentStrategy'),
      items: [],
    },
    {
      icon: 'phone',
      label: 'Admin',
      to: '/admin',
      hasError: isTabWithError('contact'),
      items: [],
    },
    {
      icon: 'lifeAssurance',
      label: 'Life assurance',
      to: '/life-assurance',
      hasError: isTabWithError('assurancePolicies'),
      items: [],
    },
    {
      icon: 'documents',
      label: 'Documents',
      to: '/documents',
      hasError: false,
      items: [],
    },
  ];
};

const getSingleApplicantRoutes = (errorPaths: ErrorPath[], module: string): ItemListType => {
  const modulesWithErrorsPaths = errorPaths.filter(isNotUndefinedErrorPath).filter((errorPath) =>
    Object.keys(errorPath).some((path) => {
      return path.startsWith(module);
    }),
  );

  const isTabWithError = (fields: string[]) => {
    return modulesWithErrorsPaths.some((moduleWithErrorPath) =>
      Object.keys(moduleWithErrorPath).some((path) => {
        return fields.some((field) => path.startsWith(`${module}.${field}`));
      }),
    );
  };

  return [
    {
      label: 'Personal details',
      to: `/personal-details`,
      hasError: isTabWithError([
        FIELDS.APPLICANT_NAME,
        FIELDS.APPLICANT_SURNAME,
        FIELDS.APPLICANT_TITLE,
        FIELDS.APPLICANT_AGE,
        FIELDS.APPLICANT_GENDER,
        FIELDS.APPLICANT_IS_PHONE_MOBILE_PEPPER,
        FIELDS.APPLICANT_IS_PHONE_WORK_PEPPER,
        FIELDS.APPLICANT_IS_PHONE_HOME_PEPPER,
        FIELDS.APPLICANT_IS_EMAIL_PEPPER,
        FIELDS.APPLICANT_ADDITIONAL_EMAIL_PEPPER,
        FIELDS.APPLICANT_PHONE_HOME,
        FIELDS.APPLICANT_PHONE_MOBILE,
        FIELDS.APPLICANT_PHONE_WORK,
        FIELDS.APPLICANT_DATE_OF_BIRTH,
      ]),
      items: [],
    },
    {
      label: 'Vulnerability',
      to: `/vulnerabilities`,
      hasError: false,
      items: [],
    },
    {
      label: 'Employment',
      to: `/employment`,
      hasError: isTabWithError([FIELDS.APPLICANT_EMPLOYMENT_STATUS]),
      items: [],
    },
    {
      label: 'Assets',
      to: `/assets`,
      hasError: false,
      items: [],
    },
    {
      label: 'Income',
      to: `/income`,
      hasError: isTabWithError([INCOME_FIELDS.TOTAL]),
      items: [],
    },
    {
      label: 'Pensions',
      to: `/pensions`,
      hasError: false,
      items: [],
    },
    {
      label: 'Expenditure',
      to: `/expenditure`,
      hasError: false,
      items: [],
    },
  ];
};

const useNavItems = () => {
  const { errorsPaths: errors } = usePepperFieldError();

  const getItems = useCallback(
    (applicants: CaseDetails['case']['applicants'], getTitleOption: (title: string) => string): ItemListType => {
      const [loandetails, credit, property, repaymentStrategy, ...rest] = getDefaultItems(errors);
      const applicantItems: ItemListType = applicants.map((applicant, i) => {
        const to = `/applicant/${i + 1}`;
        const items = getSingleApplicantRoutes(errors, `applicants.${i}`).map((sar) => ({
          ...sar,
          to: `${to}${sar.to}`,
        }));
        return {
          icon: 'person',
          label: `${getTitleOption(applicant.title)} ${applicant.name} ${applicant.surname}`,
          to,
          items,
          hasError: items.some((item) => item.hasError),
        };
      });

      return [loandetails, ...applicantItems, credit, property, repaymentStrategy, ...rest];
    },
    [errors],
  );

  return [getItems];
};

export default useNavItems;
